import axios from 'axios'
import { ethers } from 'ethers'
import type { JsonRpcSigner } from '@ethersproject/providers'
import { useWeb3Connection } from '../../../../../web3/Web3Provider'
import { config } from '../../../../app/config'
import { Network, SupportedNetwork } from '@spritz-finance/sdk'
import { useGasPriceLazyQuery } from '../../../../../graphql/generated/graphql'

type TransactionSpeed = {
    safeLow: number
    standard: number
    fast: number
    fastest: number
}

type GasStationResponse = {
    blockTime: number
    blockNumber: number
} & TransactionSpeed

type SpeedOption = keyof TransactionSpeed

const networkSpeed = {
    [Network.Polygon]: 'instant',
    [Network.Binance]: 'instant',
    [Network.Ethereum]: 'fast',
    [Network.Avalanche]: 'instant',
    [Network.Optimism]: 'instant',
    [Network.Arbitrum]: 'instant',
}

export const useGasPrice = () => {
    const { account, provider, connectedNetwork } = useWeb3Connection()
    const [getPrice] = useGasPriceLazyQuery({
        variables: {
            network: connectedNetwork ?? '',
        },
        fetchPolicy: 'network-only',
    })
    const signer = provider?.getSigner(account)

    const gasPriceFetcher: Record<
        SupportedNetwork,
        ((signer: JsonRpcSigner, speed?: SpeedOption) => Promise<string>) | null
    > = {
        [Network.Polygon]: getGasPricePolygon,
        [Network.Binance]: getGasPriceBSC,
        [Network.Avalanche]: null,
        [Network.Optimism]: null,
        [Network.Arbitrum]: null,
        [Network.Ethereum]: null,
    }

    const _getGasPrice = async () => {
        const { data } = await getPrice()
        const speeds = data?.gasPrice?.speeds ?? null
        if (!speeds) return null

        const price = speeds[networkSpeed[connectedNetwork!]] ?? null
        if (price) return price

        return signer && connectedNetwork && gasPriceFetcher[connectedNetwork]
            ? gasPriceFetcher[connectedNetwork]?.(signer)
            : null
    }
    return _getGasPrice
}

const getGasPricePolygon = async (signer: JsonRpcSigner, speed: SpeedOption = 'fastest') => {
    try {
        const response = await axios.get<GasStationResponse>(
            'https://gasstation-mainnet.matic.network/'
        )
        const gas = response.data[speed]
        return ethers.utils.parseUnits(gas.toString(), 'gwei').toString()
    } catch (error) {
        const gasPrice = await signer.getGasPrice()
        return gasPrice.toString()
    }
}

const getGasPriceBSC = async (signer: JsonRpcSigner) => {
    try {
        const response = await axios.get(
            `https://api.bscscan.com/api?module=gastracker&action=gasoracle&apikey=${config.bscScanKey}`
        )
        if (response?.data?.message !== 'OK') throw new Error()
        const fastGasPrice = response.data?.result?.FastGasPrice
        return ethers.utils.parseUnits(fastGasPrice, 'gwei').toString()
    } catch (error) {
        const gasPrice = await signer.getGasPrice()
        return gasPrice.toString()
    }
}
